import { Form } from 'radix-ui';
import { type z } from 'zod';
import { forwardRef, useState, type ComponentProps, type ReactElement } from 'react';
import { Flex, TextField } from '@radix-ui/themes';
import { cn, iconSizeClasses } from '@/lib/utils';
import { getZodErrorMessage } from '@/lib/validation';
import { FieldError } from './FieldError';
import { Label } from './Label';
import { Text } from './Text';

interface InputProps extends ComponentProps<typeof TextField.Root> {
  label?: React.ReactNode;
  error?: string;
  helperText?: string;
  name: string;
  schema?: z.ZodSchema<unknown>;
  icon?: ReactElement;
  iconSize?: keyof typeof iconSizeClasses;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      className,
      name,
      schema,
      onBlur,
      onChange,
      icon,
      iconSize = 'sm',
      ...textFieldProps
    },
    ref
  ) => {
    const [touched, setTouched] = useState(false);
    const [zodError, setZodError] = useState<string | undefined>();

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setTouched(true);
      if (schema) {
        const errorMessage = getZodErrorMessage(schema, e.target.value);
        setZodError(errorMessage);
      }
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (touched && schema) {
        const errorMessage = getZodErrorMessage(schema, e.target.value);
        setZodError(errorMessage);
      }
      onChange?.(e);
    };

    const displayError = error ?? zodError;

    return (
      <Form.Field name={name}>
        <Flex direction="column" gap="2">
          {label && (
            <Label htmlFor={name} icon={icon} iconSize={iconSize}>
              {label}
            </Label>
          )}
          <TextField.Root
            ref={ref}
            id={name}
            className={cn('w-full', className)}
            onChange={handleChange}
            onBlur={handleBlur}
            {...textFieldProps}
          />
          {displayError && <FieldError message={displayError} />}
          {helperText && !displayError && (
            <Text size="1" color="gray">
              {helperText}
            </Text>
          )}
        </Flex>
      </Form.Field>
    );
  }
);

Input.displayName = 'Input';

export { Input };
export type { InputProps };
